"use client";

import {
  extractData,
  postCarmodelInrestedCars,
  postSearchGetPriceRangeByBrands,
} from "@/api";
import { Brand, CarModel } from "@/api/generated/BMWInterfaces";
import { Desktop, Mobile, useIsDesktop } from "@/common/MediaQueries";
import { svgIcons } from "@/common/SvgIcons";
import { calculate } from "@/common/calculator";
import { CardCar } from "@/components/shared/CardCar";
import NextLink from "@/components/shared/NextLink";
import ArrowSwiperIcon from "@/components/shared/icons/ArrowSwiperIcon";
import FeatureIcon from "@/components/shared/icons/Features/FeatureIcon";
import { minMaxPriceStore, productCarStore } from "@/store";
import colors from "@/theme/colors";
import theme from "@/theme/theme";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import styled from "@emotion/styled";
import {
  Box,
  Button,
  ButtonBase,
  Container,
  Dialog,
  Grid,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  tableCellClasses,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { sendGTMEvent } from "@next/third-parties/google";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import rtlPlugin from "stylis-plugin-rtl";
import "swiper/css";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { CustomBreadcrumbs } from "../Common/BreadCrumbs/CustomBreadcrumbs";
import TermsAndConditions from "./TermsAndConditions";

const ProductPageContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ProductPageWrapper = styled(Container)`
  display: flex;
  flex-direction: column;
  padding: 0px 120px 0px 120px;
  justify-content: center;
  max-width: 1600px;
  ${theme.breakpoints.down("md")} {
    padding: 0px;
  }
`;
const NavigationRow = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  padding: 16px 32px 16px 16px;
  ${theme.breakpoints.down("md")} {
    padding: 10px;
    padding-left: 20px;
    padding-right: 20px;
  }
  justify-content: space-between;
`;
const NavigationLinks = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 10px;
`;

const SectionsWrapper = styled.div`
  overflow: hidden;
`;

const SectionLeftWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0px 0 16px;
  ${theme.breakpoints.down("sm")} {
    display: none;
  }
`;

const SectionRightWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 650px;
  direction: rtl;
  overflow-y: scroll;
  max-width: 456px;
  flex-grow: 1;
  padding: 32px;
  border: 1px solid #f2f2f2;
  ${theme.breakpoints.down("sm")} {
    overflow: hidden;
    padding-left: 0px;
    padding-right: 0px;
    max-height: none;
  }
`;

const SectionRow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 8px 0;
  ${theme.breakpoints.down("sm")} {
    padding: 8px 16px;
  }
`;

const SectionBudget = styled.div`
  display: flex;
  flex-direction: row;
  ${theme.breakpoints.down("sm")} {
    margin-left: 15px;
    margin-right: 15px;
  }
  justify-content: space-between;
  gap: 16px;
`;

const SectionColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const SpecificationsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  gap: 16px;
`;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: colors.white,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: colors.white,
    color: colors.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const MainPhoto = styled(Image)`
  position: relative !important;
  width: 100% !important;
  object-fit: contain;
`;

const SectionImageContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: calc(33.33% - 10px);
  padding-bottom: 25%;
  overflow: hidden;
`;

const SectionImageWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 0px 16px 0px;
  gap: 10px;
  width: 100%;
`;

const ActionWraapper = styled(Box)(({}) => ({
  display: "flex",
  zIndex: 2,
  borderBottom: "1px solid #f2f2f2",
  borderLeft: "1px solid #f2f2f2",
  borderRight: "1px solid #f2f2f2",
  flexDirection: "column",
  position: "relative",
  backgroundColor: `${colors.white}`,
  bottom: 0,
  gap: 8,
  width: "100%",
  padding: "8px 16px 16px 16px",
  boxShadow: "0px 2px 6px 0px rgba(0, 36, 78, 0.3)",

  [theme.breakpoints.down("sm")]: {
    position: "fixed",
  },
}));

const ParentSectionWrapper = styled.div`
  display: flex;
  gap: 32px;
  ${theme.breakpoints.down("sm")} {
    gap: 0px;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
`;

const ButtonAction = styled(Button)`
  height: 50px;
`;

const ImageWrapper = styled(Image)`
  width: 100% !important;
  height: 225px !important;
  position: relative !important;
  object-fit: contain;
`;

const RectanglePayment = styled(ToggleButton)(
  ({ valuepayment, payments }: any) => ({
    minWidth: 60,
    height: 40,
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${colors.darkBlue}`,
    margin: 0,
    backgroundColor: valuepayment === payments ? colors.brandBlue : "white",
    "&:hover": {
      backgroundColor: colors.brandBlue,
      "& .hover-text": {
        color: "white",
      },
    },
    "&.Mui-selected": {
      backgroundColor: colors.brandBlue,
      color: colors.white,
      "& .hover-text": {
        color: colors.white,
      },
      "&:hover": {
        backgroundColor: colors.brandBlue, // Keep the same background when hovered while selected
      },
    },
  })
);

const CustomSwiperSlide = styled(SwiperSlide)(({ theme }) => ({
  width: "100% !important",
  height: "225px",
  "&.swiper-slide-active": {
    width: "100% !important",
  },
}));

const CustomSwiperSlideCar = styled(SwiperSlide)(({ theme }) => ({
  cursor: "pointer",
  width: "174px !important",
  "&.swiper-slide-active": {
    width: "174px !important",
  },
}));

const StyledSwiper = styled(Swiper)`
  ${theme.breakpoints.up("md")} {
    height: 132px;
    width: 100%;

    .swiper-button-next,
    .swiper-button-prev {
      position: absolute;
      z-index: 1;
      border-radius: 36px;
      border: 1px #cddaea;
      background: var(--Grey-6, #f2f2f2);
      box-shadow: 0px 5px 15px 0px rgba(0, 36, 78, 0.21);
      height: 30px;
      width: 30px;
    }
    .swiper-wrapper {
      width: 552px;
    }
    .swiper-button-prev {
      inset: 60px 0 auto auto;
    }
    .swiper-button-next {
      inset: 60px auto auto 0;
      rotate: 180deg;
    }
  }

  ${theme.breakpoints.down("sm")} {
    padding-bottom: 30px;
  }
`;
const StyledRecomandedSwiper = styled(Swiper)`
  ${theme.breakpoints.up("md")} {
    width: 100%;
    padding-bottom: 30px;

    .swiper-button-next,
    .swiper-button-prev {
      position: absolute;
      top: 40px;
      z-index: 1;
      border-radius: 36px;
      border: 1px #cddaea;
      background: var(--Grey-6, #f2f2f2);
      box-shadow: 0px 5px 15px 0px rgba(0, 36, 78, 0.21);
      height: 20px;
      width: 20px;
    }

    .swiper-button-prev {
      left: 0;
    }
    .swiper-button-next {
      right: 0;
      rotate: 180deg;
    }
  }

  ${theme.breakpoints.down("sm")} {
    padding-bottom: 30px;
  }
`;
const CustomeSwiperSlide = styled(SwiperSlide)`
  height: auto;

  ${theme.breakpoints.up("md")} {
    width: 250px !important;
  }
`;
const LinkWrapper = styled(NextLink)`
  text-decoration-line: none;
  height: 100%;
  display: block;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  margin-bottom: 60px;
  ${theme.breakpoints.down("sm")} {
    padding-inline-start: 16px;
  }
`;

function createData(name: string, feature: string) {
  return { name, feature };
}

type CarModelProps = {
  car: CarModel;
  brands: Brand[];
};
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [rtlPlugin],
});

export const ProductPage = ({ car, brands }: CarModelProps) => {
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const tradingType = car.calculatorData.tradingType;

  const handleAdvancedpaymentAndMaxPayment = useMemo(() => {
    return (advanced: boolean) => {
      const price = car.price;
      if (advanced) {
        if (tradingType === 0) {
          return 0; // price advanced is according to new request
        } else {
          return price <= 350000 ? 0.15 * price : price - 300000;
        }
      } else {
        if (tradingType === 0) {
          return price <= 400000 ? price - 20000 : 380000;
        }
        return 0.4 * totalLoanAmount;
      }
    };
  }, [car.calculatorData]);

  const [advancedPayment, setAdvancedPayment] = useState<number>(
    handleAdvancedpaymentAndMaxPayment(true)
  );

  const [endingPayment, setEndingPayment] = useState(0);
  const [payments, setPayments] = useState(60);
  const paymentsSteps = [60, 48, 36, 24, 12];
  const [mounthPayment, setMounthPayment] = useState(0);
  const [totalLoanAmount, setTotalLoanAmount] = useState(0);
  const [recomended, setRecomended] = useState<CarModel[]>([]);
  const [openTermsAndConditionsPopup, setOpenTermsAndConditionsPopup] =
    useState(false);
  const [selectedPicture, setSelectedPicture] = useState(car.images[0]);
  const handleRecommended = async () => {
    const getRecomanded = await postCarmodelInrestedCars({
      carModelId: car.id,
      brandId: [car.brandId],
      minPrice:
        minMaxPriceStore()[0] !== 0
          ? minMaxPriceStore()[0]
          : Math.max(car.price - 50000, 0),
      maxPrice:
        minMaxPriceStore()[1] !== 0 ? minMaxPriceStore()[1] : car.price + 50000,
    }).then(extractData);
    setRecomended(getRecomanded);
  };
  useEffect(() => {
    const getCalculated = calculate(
      car.price,
      car.calculatorData.tradingType ? false : true,
      advancedPayment as number,
      car.calculatorData.bankIntrest,
      car.calculatorData.primeIntrest,
      car.calculatorData.tradingType === 0
        ? Math.max(
            Math.min(
              car.price >= 400000
                ? (400000 - advancedPayment) / 2
                : (car.price - advancedPayment) / 2,
              endingPayment
            ),
            0
          )
        : Math.max(
            Math.min((car.price - advancedPayment) * 0.4, endingPayment),
            0
          ),
      payments
    );
    setMounthPayment(getCalculated.monthlyPayments);
    setAdvancedPayment(advancedPayment);
    setTotalLoanAmount(getCalculated.totalLoanAmount);
  }, [advancedPayment, endingPayment, payments, mounthPayment]);

  const handleChangeAdvancedPayment = (
    event: Event,
    newValue: number | number[]
  ) => {
    setAdvancedPayment(Number(newValue) as number);
  };

  const handlePayment = (payment: number) => {
    if (!payment) return;
    setPayments(payment);
  };

  const handleChangeEndingPayment = (
    event: Event,
    newValue: number | number[]
  ) => {
    setEndingPayment(newValue as number);
  };
  const getToAllModels = async () => {
    sendGTMEvent({
      action: "click",
      event: "all_models_button_clicks",
    });
  };
  const rows = [
    createData("סדרה", car?.brand.name),
    createData("דגם", car?.modelName),
    createData("צבע", car?.colorName),
    createData("ריפוד", car?.upholseteryName),
    createData("מספר דלתות", car?.doorsNumber?.toString()),
    createData("מספר מושבים", car?.seatsNumber?.toString()),
    createData("היסטוריית טיפולים מלאה", car.fullHistoryRecord?.toString()),
    createData("בעלויות קודמות (יד)", car?.handNumber?.toString()),
    createData("תוכנית מכוניות", car?.carPlan),
    createData("מקוריות", car?.originality?.toString()),
  ];

  useEffect(() => {
    if (car) {
      handleRecommended();
    }
  }, [car]);
  useEffect(() => {
    const getPriceRange = async () => {
      let selectedIds = brands.map((brand) => brand.id);
      const res = await postSearchGetPriceRangeByBrands(selectedIds);
      if (res) {
        setMinPrice(res.data.body.minPrice);
        setMaxPrice(res.data.body.maxPrice);
      }
    };

    getPriceRange();
  }, []);
  const findIconByTitle = (title: string) => {
    const icon = svgIcons.find((icon) => icon.name === title);
    return icon ? icon.svg : null; // or a fallback icon if you prefer
  };
  const pushToContact = () => {
    sendGTMEvent({
      action: "click",
      event: "book_an_appointment_clicks",
    });
    productCarStore(car);
  };

  const contactUs = () => {
    sendGTMEvent({
      action: "click",
      event: "model_page_contact_clicks",
    });
    productCarStore(undefined);
  };
  const isDesktop = useIsDesktop();
  return (
    <ProductPageContainer>
      <ProductPageWrapper>
        <NavigationRow style={{ flexDirection: "row" }}>
          <NavigationLinks>
            <CustomBreadcrumbs
              parents={[
                { title: "BMW", url: "https://www.bmw.co.il/he" },
                { title: "טרייד אין", url: "/" },
                { title: "חיפוש רכב", url: "/inventory" },
                {
                  title:
                    car.brand.name +
                    " " +
                    car.modelName +
                    " " +
                    car.finishingLevelName,
                  url: `/product/${car.id}`,
                },
              ]}
            />
          </NavigationLinks>
          {isDesktop && (
            <NextLink
              passHref
              legacyBehavior
              href={`/inventory?minPrice=${minPrice}&maxPrice=${maxPrice}`}
            >
              <ButtonBase
                onClick={getToAllModels}
                style={{
                  color: colors.blue,
                  textDecoration: "underline",
                  justifyContent: "flex-end",
                }}
              >
                <Box sx={{ display: "flex" }}>
                  <Typography
                    fontSize={16}
                    fontWeight={700}
                    sx={{ textDecoration: "underline" }}
                    color={colors.blue}
                  >
                    חזרה לעמוד הדגמים
                  </Typography>
                  <Image
                    src="/assets/cheveronLeft.svg"
                    alt=""
                    width={25}
                    height={25}
                  />
                </Box>
              </ButtonBase>
            </NextLink>
          )}
        </NavigationRow>

        <ParentSectionWrapper>
          <SectionsWrapper style={{ width: "456px" }}>
            <SectionRightWrapper>
              <SectionRow>
                <Typography component={"h1"} fontSize={24} fontWeight={500}>
                  {car?.brand.name} {car?.modelName} {car?.finishingLevelName}
                </Typography>
              </SectionRow>
              <SectionRow>
                <Typography
                  fontSize={14}
                  fontWeight={300}
                  color={colors.bmwBlue}
                >
                  {car.year}
                </Typography>
                <Image
                  src="/assets/line.svg"
                  alt="search"
                  width={20}
                  height={20}
                />
                <Typography
                  fontSize={14}
                  fontWeight={300}
                  color={colors.bmwBlue}
                >
                  {car?.handNumber}
                </Typography>
                <Image
                  src="/assets/line.svg"
                  alt="search"
                  width={20}
                  height={20}
                />
                <Typography
                  fontSize={14}
                  fontWeight={300}
                  color={colors.bmwBlue}
                >
                  אולם {car?.showRoom.name}
                </Typography>
              </SectionRow>
              <Mobile>
                <Swiper
                  modules={[Pagination, A11y]}
                  spaceBetween={0}
                  style={{ margin: 0, display: "flex", width: "100%" }}
                  slidesPerView={1}
                  pagination={{ clickable: true }}
                >
                  {car.images?.slice().map((image, i) => (
                    <CustomSwiperSlide key={i}>
                      <ImageWrapper
                        src={image.imageUrl}
                        alt={image.alt}
                        fill
                        style={{ objectFit: "cover" }}
                      />
                    </CustomSwiperSlide>
                  ))}
                </Swiper>
              </Mobile>
              <SectionRow>
                <Grid container spacing={4} columns={4} marginTop={0.1}>
                  {car.features?.features.map((spec, index) => (
                    <Grid
                      item
                      xs={2}
                      key={spec.text}
                      style={{
                        display: "flex",

                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <FeatureIcon
                        style={{
                          minWidth: 16, // Adjust the size based on your icon
                          width: 16,
                          height: 16,
                          flexShrink: 0, // Prevent shrinking
                        }}
                      >
                        {findIconByTitle(spec.typeName)}
                      </FeatureIcon>

                      <Typography
                        sx={{ unicodeBidi: "plaintext" }}
                        fontSize={14}
                        fontWeight={300}
                      >
                        {spec.text}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </SectionRow>
              {!isDesktop && (
                <NextLink
                  passHref
                  legacyBehavior
                  href={`/inventory?minPrice=${minPrice}&maxPrice=${maxPrice}`}
                >
                  <ButtonBase
                    onClick={getToAllModels}
                    style={{
                      color: colors.blue,
                      textDecoration: "underline",
                      justifyContent: "flex-start",
                      paddingInlineStart: "16px",
                      marginTop: "16px",
                    }}
                  >
                    <Box sx={{ display: "flex" }}>
                      <Typography
                        fontSize={16}
                        fontWeight={700}
                        sx={{ textDecoration: "underline" }}
                        color={colors.blue}
                      >
                        חזרה לעמוד הדגמים
                      </Typography>
                      <Image
                        src="/assets/cheveronLeft.svg"
                        alt=""
                        width={25}
                        height={25}
                      />
                    </Box>
                  </ButtonBase>
                </NextLink>
              )}
              <SectionRow>
                <SpecificationsWrapper>
                  <Typography component={"h2"} fontSize={24} fontWeight={300}>
                    מידע נוסף על הרכב
                  </Typography>
                </SpecificationsWrapper>
              </SectionRow>
              <SectionRow
                style={isDesktop ? { padding: "8px" } : { padding: "8px 16px" }}
              >
                <TableContainer component={Paper}>
                  <Table
                    role="presentation"
                    sx={{ width: "100%" }}
                    aria-label="customized table"
                  >
                    <TableBody>
                      {rows.map((row, index) => (
                        <StyledTableRow key={index}>
                          <StyledTableCell
                            sx={{
                              "&.MuiTableCell-root": {
                                width: "62%",
                                fontWeight: 300,
                                textAlign: "right",
                              },
                            }}
                            key={index}
                            scope="row"
                          >
                            {row.name}
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ fontWeight: 500, textAlign: "right" }}
                            component="th"
                            key={row.name}
                          >
                            {row.feature}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </SectionRow>
              <SectionRow style={{ justifyContent: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    marginBottom: "34px",
                    textAlign: "center",
                  }}
                >
                  <Typography component={"h2"} fontSize={24} fontWeight={300}>
                    הצעת מימון משתלמת
                  </Typography>
                  <Typography fontSize={16} fontWeight={300}>
                    המחירים לאפשרויות המימון להדגמה בלבד
                  </Typography>
                </Box>
              </SectionRow>
              <SectionRow style={{ justifyContent: "center" }}>
                <SectionColumn>
                  <Typography fontSize={20} fontWeight={600}>
                    {tradingType === 0
                      ? Math.round(Math.max(mounthPayment, 0)).toLocaleString(
                          "en-US",
                          {
                            currency: "ILS",
                            style: "currency",
                            maximumSignificantDigits: 5,
                          }
                        )
                      : Math.max(mounthPayment, 0).toLocaleString("en-US", {
                          currency: "ILS",
                          style: "currency",
                          maximumSignificantDigits: 4,
                        })}
                  </Typography>
                  <Typography fontSize={17} fontWeight={300}>
                    תשלום חודשי
                  </Typography>
                </SectionColumn>
              </SectionRow>
              <SectionBudget>
                <Typography fontSize={16} color={colors.black} fontWeight={300}>
                  סכום המקדמה
                </Typography>
                <Typography fontSize={19} color={colors.black} fontWeight={400}>
                  {advancedPayment.toLocaleString("en-US", {
                    style: "currency",
                    currency: "ILS",
                    maximumFractionDigits: 0,
                  })}
                </Typography>
              </SectionBudget>
              <SectionRow style={isDesktop ? {} : { padding: "8px 24px" }}>
                <CacheProvider value={cacheRtl}>
                  <Slider
                    value={advancedPayment}
                    onChange={handleChangeAdvancedPayment}
                    max={
                      tradingType === 0
                        ? handleAdvancedpaymentAndMaxPayment(false)
                        : car.price
                    }
                    min={handleAdvancedpaymentAndMaxPayment(true)}
                    step={1000}
                    getAriaValueText={() =>
                      "סכום מקדמה " +
                      advancedPayment.toLocaleString("en-US", {
                        style: "currency",
                        currency: "ILS",
                        maximumFractionDigits: 0,
                      })
                    }
                    aria-label="סכום מקדמה"
                    valueLabelDisplay="off"
                  />
                </CacheProvider>
              </SectionRow>
              <SectionBudget>
                <Typography fontSize={16} color={colors.black} fontWeight={300}>
                  {handleAdvancedpaymentAndMaxPayment(true).toLocaleString(
                    "en-US",
                    {
                      style: "currency",
                      currency: "ILS",
                      maximumFractionDigits: 0,
                    }
                  )}
                </Typography>
                <Typography fontSize={16} color={colors.black} fontWeight={300}>
                  {tradingType === 0
                    ? handleAdvancedpaymentAndMaxPayment(false).toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: "ILS",
                          maximumFractionDigits: 0,
                        }
                      )
                    : car.price.toLocaleString("en-US", {
                        style: "currency",
                        currency: "ILS",
                        maximumFractionDigits: 0,
                      })}
                </Typography>
              </SectionBudget>
              <SectionRow
                style={{
                  paddingInlineEnd: 0,
                  paddingTop: 32,
                  justifyContent: "flex-start",
                }}
              >
                <Typography fontSize={16} color={colors.black} fontWeight={300}>
                  מס׳ חודשים
                </Typography>
              </SectionRow>
              <Box display="flex" justifyContent="center">
                <ToggleButtonGroup
                  value={payments}
                  exclusive
                  onChange={(_, valuepayment) => handlePayment(valuepayment)}
                  aria-label="payment steps"
                  color="primary"
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                  }}
                >
                  {paymentsSteps.map((valuepayment) => (
                    <RectanglePayment
                      key={valuepayment}
                      value={valuepayment}
                      payments={payments}
                      sx={{
                        "&:hover": {
                          backgroundColor: colors.brandBlue,
                          "& .hover-text": {
                            color: colors.white,
                          },
                        },
                      }}
                    >
                      <Typography
                        className="hover-text"
                        fontSize={16}
                        sx={{
                          fontSize: 16,
                          color:
                            valuepayment === payments
                              ? colors.white
                              : colors.black,
                        }}
                      >
                        {valuepayment}
                      </Typography>
                    </RectanglePayment>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <SectionBudget
                style={{
                  paddingTop: 32,
                }}
              >
                <Typography fontSize={16} color={colors.black} fontWeight={300}>
                  תשלום בסוף התקופה
                </Typography>

                <Typography fontSize={19} color={colors.black} fontWeight={400}>
                  {Math.max(
                    Math.min(
                      car.tradingType === 0
                        ? (car.price >= 400000
                            ? 400000 - advancedPayment
                            : car.price - advancedPayment) / 2
                        : (car.price - advancedPayment) * 0.4,
                      endingPayment
                    ),
                    0
                  ).toLocaleString("en-US", {
                    style: "currency",
                    currency: "ILS",
                    maximumFractionDigits: 0,
                  })}
                </Typography>
              </SectionBudget>
              <SectionRow style={isDesktop ? {} : { padding: "8px 24px" }}>
                <CacheProvider value={cacheRtl}>
                  <Slider
                    value={endingPayment}
                    onChange={handleChangeEndingPayment}
                    max={
                      car.tradingType === 0
                        ? Math.max(
                            (car.price >= 400000
                              ? 400000 - advancedPayment
                              : car.price - advancedPayment) / 2,
                            0
                          )
                        : Math.max((car.price - advancedPayment) * 0.4, 0)
                    }
                    min={0}
                    step={1000}
                    getAriaValueText={() =>
                      "תשלום בסוף התקופה " +
                      endingPayment.toLocaleString("en-US", {
                        style: "currency",
                        currency: "ILS",
                        maximumFractionDigits: 0,
                      })
                    }
                    valueLabelDisplay="off"
                    aria-label="תשלום בסוף התקופה"
                  />
                </CacheProvider>
              </SectionRow>

              <SectionBudget>
                <Typography fontSize={16} color={colors.black} fontWeight={300}>
                  ₪0
                </Typography>
                <Typography fontSize={16} color={colors.black} fontWeight={300}>
                  {(car.tradingType === 0
                    ? Math.max(
                        (car.price >= 400000
                          ? 400000 - advancedPayment
                          : car.price - advancedPayment) / 2,
                        0
                      )
                    : Math.max((car.price - advancedPayment) * 0.4, 0)
                  ).toLocaleString("en-US", {
                    style: "currency",
                    currency: "ILS",
                    maximumSignificantDigits: 5,
                  })}
                </Typography>
              </SectionBudget>
              <ButtonBase
                sx={{
                  justifyContent: "flex-start",
                  marginTop: "16px",
                  paddingInlineStart: isDesktop ? "" : "16px",
                }}
                onClick={() => setOpenTermsAndConditionsPopup(true)}
              >
                <Typography
                  sx={{ textDecoration: "underline" }}
                  fontSize={14}
                  color={colors.blue}
                  fontWeight={700}
                >
                  לתנאים ותקנון
                </Typography>
              </ButtonBase>
            </SectionRightWrapper>
            <Desktop>
              <ActionWraapper>
                <Typography
                  component={"h3"}
                  tabIndex={0}
                  fontSize={16}
                  color={colors.black}
                  fontWeight={400}
                >
                  מחיר
                </Typography>
                <ActionRow>
                  <Typography fontSize={24} fontWeight={500}>
                    {car.price.toLocaleString("en-US", {
                      style: "currency",
                      currency: "ILS",
                      maximumSignificantDigits: 3,
                    })}
                  </Typography>
                  <Typography fontSize={14} fontWeight={300}>
                    כולל מע״מ
                  </Typography>
                </ActionRow>
                <ActionRow>
                  <NextLink style={{ flexGrow: 2 }} passHref href="/contact">
                    <ButtonAction
                      role="link"
                      variant="contained"
                      color="primary"
                      style={{ width: "100%" }}
                      onClick={pushToContact}
                    >
                      <Typography fontSize={14} color={colors.white}>
                        תיאום פגישה
                      </Typography>
                    </ButtonAction>
                  </NextLink>
                  <NextLink style={{ flexGrow: 1 }} passHref href="/contact">
                    <ButtonAction
                      variant="contained"
                      style={{
                        width: "100%",
                        backgroundColor: "rgba(77, 77, 77, 1)",
                      }}
                      onClick={contactUs}
                    >
                      <Typography fontSize={14} color={colors.white}>
                        צור קשר
                      </Typography>
                      <Image
                        style={{ marginRight: 4 }}
                        src="/assets/call.svg"
                        alt="phone"
                        width={20}
                        height={20}
                      />
                    </ButtonAction>
                  </NextLink>
                </ActionRow>
              </ActionWraapper>
            </Desktop>
          </SectionsWrapper>
          {isDesktop && (
            <SectionsWrapper style={isDesktop ? { width: "750px" } : {}}>
              <SectionLeftWrapper>
                {car?.images && (
                  <MainPhoto
                    src={selectedPicture?.imageUrl}
                    alt={selectedPicture?.alt}
                    width={750}
                    height={422}
                    priority
                  />
                )}

                <SectionImageWrapper>
                  <SectionImageContainer style={{ maxWidth: "100%" }}>
                    <StyledSwiper
                      navigation={{
                        nextEl: ".swiper-button-next",
                        prevEl: ".swiper-button-prev",
                      }}
                      modules={[Navigation, A11y]}
                      slidesPerView={4}
                      spaceBetween={15}
                      slidesOffsetAfter={100}
                    >
                      {car.images?.map((image, i) => (
                        <CustomSwiperSlideCar
                          onClick={() => setSelectedPicture(image)}
                          key={i}
                        >
                          <Image
                            key={i}
                            src={image.imageUrl}
                            alt={image.alt}
                            width={174}
                            height={132}
                            style={{ objectFit: "cover" }}
                          />
                        </CustomSwiperSlideCar>
                      ))}
                      <ButtonBase className="swiper-button-next">
                        <ArrowSwiperIcon />
                      </ButtonBase>
                      <ButtonBase className="swiper-button-prev">
                        <ArrowSwiperIcon />
                      </ButtonBase>
                    </StyledSwiper>
                  </SectionImageContainer>
                </SectionImageWrapper>
              </SectionLeftWrapper>
            </SectionsWrapper>
          )}
        </ParentSectionWrapper>
        <SectionRow
          style={{
            marginTop: 20,
            justifyContent: "flex-start",
            paddingRight: isDesktop ? 0 : 16,
          }}
        >
          <Typography component={"h2"} fontSize={24} fontWeight={300}>
            הצעות שכדאי לבדוק
          </Typography>
        </SectionRow>
        <SectionRow
          style={{
            marginBottom: 20,
            padding: "8px 0 8px",
            justifyContent: "initial",
          }}
        >
          <Box
            style={{
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              justifyContent: "center",
              [theme.breakpoints.up("md")]: {
                display: "none",
              },
            }}
          >
            <StyledRecomandedSwiper
              key={`${isDesktop}-${recomended.length}`}
              enabled={isDesktop ? recomended.length > 4 : true}
              spaceBetween={20}
              slidesPerView={1.4}
              touchRatio={1}
              slidesOffsetBefore={0}
              slidesPerGroup={1}
              slidesOffsetAfter={50}
              style={{
                paddingInlineStart: isDesktop ? "32px" : "16px",
                paddingBottom: "16px",
                paddingTop: "21px",
              }}
            >
              {recomended.map((car) => (
                <CustomeSwiperSlide
                  onClick={() =>
                    sendGTMEvent({
                      action: "click",
                      event: "model_clicks",
                      model_type: car.modelName,
                    })
                  }
                  key={car.id}
                >
                  <LinkWrapper href={`/product/${car.id}`}>
                    <CardCar car={car} />
                  </LinkWrapper>
                </CustomeSwiperSlide>
              ))}
            </StyledRecomandedSwiper>
          </Box>
        </SectionRow>
        <NextLink
          passHref
          legacyBehavior
          href={`/inventory?minPrice=${minPrice}&maxPrice=${maxPrice}`}
        >
          <ButtonBase
            onClick={getToAllModels}
            style={{
              color: colors.blue,
              textDecoration: "underline",
              justifyContent: "flex-start",
            }}
          >
            <Row>
              <Typography fontSize={18} fontWeight={700} color={colors.blue}>
                חזרה לעמוד הדגמים
              </Typography>
              <Image
                src="/assets/cheveronLeft.svg"
                alt=""
                width={25}
                height={25}
              />
            </Row>
          </ButtonBase>
        </NextLink>
        {!useIsDesktop() && (
          <ActionWraapper>
            <Typography fontSize={16} color={colors.black} fontWeight={400}>
              מחיר
            </Typography>
            <ActionRow>
              <Typography fontSize={24} fontWeight={500}>
                {car.price.toLocaleString("en-US", {
                  style: "currency",
                  currency: "ILS",
                  maximumSignificantDigits: 3,
                })}
              </Typography>
              <Typography fontSize={14} fontWeight={300}>
                כולל מע״מ
              </Typography>
            </ActionRow>
            <ActionRow>
              <NextLink passHref href="/contact">
                <ButtonAction
                  style={{ flexGrow: 2 }}
                  variant="contained"
                  color="primary"
                  onClick={pushToContact}
                >
                  <Typography fontSize={14} color={colors.white}>
                    תיאום פגישה
                  </Typography>
                </ButtonAction>
              </NextLink>
              <ButtonAction
                variant="contained"
                style={{
                  flexGrow: 1,
                  backgroundColor: "rgba(77, 77, 77, 1)",
                }}
                onClick={() =>
                  sendGTMEvent({
                    action: "click",
                    event: "model_page_contact_clicks",
                  })
                }
                href={`tel:${car?.showRoom?.phone ?? ""}`}
              >
                <Typography fontSize={14} color={colors.white}>
                  צור קשר
                </Typography>
                <Image
                  style={{ marginRight: 4 }}
                  src="/assets/call.svg"
                  alt="phone"
                  width={20}
                  height={20}
                />
              </ButtonAction>
            </ActionRow>
          </ActionWraapper>
        )}
      </ProductPageWrapper>
      <Dialog
        PaperProps={{ style: { borderRadius: "20px" } }}
        onClose={() => setOpenTermsAndConditionsPopup(false)}
        open={openTermsAndConditionsPopup}
      >
        <TermsAndConditions onClose={setOpenTermsAndConditionsPopup} />
      </Dialog>
    </ProductPageContainer>
  );
};
